/**
 * Recovers a tab that was left open across a deploy.
 *
 * Every module is a lazy route, so the HTML a tab loaded hours ago points at
 * hashed chunks that the next deploy deletes from the server. Opening a module
 * then rejects the dynamic import and the user sees a blank screen or the error
 * boundary — a failure they read as "Numtable is broken" when the fix is only to
 * load the current build. Reloading once turns that into a flicker.
 *
 * The guard exists because the same reload would spin if a chunk is genuinely
 * missing from the live build too: one attempt per minute, then the error is
 * allowed through so the boundary can report something rather than the tab
 * reloading forever.
 */
const ATTEMPT_KEY = 'numtable:stale-build-reload';
const RETRY_AFTER_MS = 60_000;

const STALE_CHUNK_MESSAGE =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i;

function isStaleChunkError(reason: unknown): boolean {
  const message = reason instanceof Error ? reason.message : typeof reason === 'string' ? reason : '';
  return STALE_CHUNK_MESSAGE.test(message);
}

/** Storage throws outright in some privacy modes, where no recovery is better than no app. */
function recentlyAttempted(): boolean {
  try {
    const previous = Number(sessionStorage.getItem(ATTEMPT_KEY));
    if (!Number.isFinite(previous) || previous === 0) return false;
    return Date.now() - previous < RETRY_AFTER_MS;
  } catch {
    return true;
  }
}

function markAttempt(): void {
  try {
    sessionStorage.setItem(ATTEMPT_KEY, String(Date.now()));
  } catch {
    /* Without storage the attempt cannot be bounded, so the check above refuses instead. */
  }
}

/** Called by the manual reset, whose whole point is to start from a clean slate. */
export function clearStaleBuildAttempt(): void {
  try {
    sessionStorage.removeItem(ATTEMPT_KEY);
  } catch {
    /* Nothing was recorded if storage is unavailable. */
  }
}

export function installStaleBuildRecovery(): void {
  const recover = () => {
    if (recentlyAttempted()) return false;
    markAttempt();
    window.location.reload();
    return true;
  };

  // Vite raises this from its preload helper before the import itself rejects.
  window.addEventListener('vite:preloadError', (event) => {
    if (recover()) event.preventDefault();
  });

  window.addEventListener('unhandledrejection', (event) => {
    if (!isStaleChunkError(event.reason)) return;
    if (recover()) event.preventDefault();
  });
}
